import { staticFilesBaseUrl } from 'app/api/api';
import { filesApi } from 'entities/file/api/filesApi';
import { makeAutoObservable, runInAction } from 'mobx';
import { ExplorerModelInterface, ExplorerEntityTypes } from 'entities/types';

export type FileData = {
  id: string;
  name: string;
  last_change_date: string;
  add_date: string;
  add_date_to_favorite: string;
  username: string;
  email: string;
  user_id: string;
  type: string;
  size: number;
  preview_url: string | null;
  source_preview_url: string | null;
  is_favorite: boolean;
  folder_id: string;
  section_id: string;
};

export type CreateFileParams = {
  file: File;
  previewFile?: File;
  section_id: string;
  folder_id?: string;
};

export type RestoreFileParams = {
  folder_id?: string;
  section_id: string;
};

export class FileModel implements ExplorerModelInterface {
  public type = ExplorerEntityTypes.FILE as const;
  public isPending = false;
  public isError = false;

  private _data: FileData | null = null;
  private _id: string;
  public get id() {
    return this._id;
  }

  public get data() {
    return this._data
      ? {
          id: this._data.id,
          section_id: this._data.section_id,
          folder_id: this._data.folder_id,
          name: this._data.name,
          last_change_date: this._data.last_change_date,
          add_date: this._data.add_date,
          add_date_to_favorite: this._data.add_date_to_favorite,
          is_favorite: this._data.is_favorite,
          type: this._data.type,
          username: this._data.username,
          size: this._data.size,
          rawPreviewUrl: this._data.preview_url,
          preview_url: this._data?.preview_url,
          source_preview_url: this._data?.source_preview_url
            ? `${staticFilesBaseUrl}${this._data.source_preview_url}`
            : null,
        }
      : null;
  }

  constructor(idOrData: FileData | string) {
    if (typeof idOrData === 'string') {
      this._id = idOrData;
    } else {
      this._id = idOrData.id;
      this._data = idOrData;
    }
    makeAutoObservable(this);
  }

  public async fetch({ in_trash }: { in_trash: string | null }) {
    runInAction(() => {
      this.isPending = true;
      this.isError = false;
    });

    try {
      const _data = await filesApi.fetch(this._id, { in_trash });

      runInAction(() => {
        this._data = _data;
        this.isPending = false;
      });
    } catch (e) {
      runInAction(() => {
        this.isPending = false;
        this.isError = true;
      });
      console.error(e);
    }
  }

  updatePreviewUrlWithCacheBuster() {
    if (this.data && this.data.source_preview_url) {
      this.data.source_preview_url = `${
        this.data.source_preview_url.split('?')[0]
      }?cacheBuster=${Date.now()}`;
    }
  }

  public async setFavorite(isFavorite: boolean) {
    runInAction(() => {
      this.isPending = true;
      this.isError = false;
    });

    try {
      await filesApi.setFavourite(this._id, isFavorite);

      runInAction(() => {
        if (this._data) {
          this._data.is_favorite = isFavorite;
        }
        this.isPending = false;
      });
    } catch (e) {
      runInAction(() => {
        this.isPending = false;
        this.isError = true;
      });
      throw e;
    }
  }
}
